/**
 * Workspace chooser (/choose-workspace).
 *
 * Two situations land here, and they need the same screen:
 *
 *  - a person with memberships in several workspaces who wants to switch;
 *  - a person with a session but NO membership in the workspace this URL names
 *    — a stale `/school/:slug` link, or someone a school has removed. AuthGate
 *    sends them here rather than showing a bare 403, because the useful answer
 *    is "here is where you DO belong".
 *
 * Switching is a full navigation, not a state change. The tenant is resolved
 * once per tab from the URL, so a hard navigation is what actually changes
 * workspace — and it keeps two tabs on two workspaces genuinely independent.
 */
import { useAccount } from '@/hooks/useSession';
import { signOut } from '@/lib/session';
import { pathInTenant, tenant } from '@/lib/tenant';
import { AuthHeading, AuthLayout } from '@/components/auth/AuthLayout';
import { AuthSplash } from '@/components/AuthGate';
import { MEMBERSHIP_ROLE_LABEL, gradeLabel } from '@cwy/config/contracts';
import { fontPx } from '@/lib/fontSize';

export function ChooseWorkspacePage() {
  const account = useAccount(true);

  if (account.isPending) return <AuthSplash />;

  const memberships = account.data?.memberships ?? [];
  const current = account.data?.tenant?.slug ?? null;

  return (
    <AuthLayout width={640}>
      <div className="flex flex-col gap-5">
        <AuthHeading
          title={memberships.length === 0 ? 'No access here' : 'Choose a workspace'}
        >
          {memberships.length === 0
            ? tenant.slug
              ? 'Your account isn’t a member of this school. If you think it should be, ask the school to add you.'
              : 'Your account isn’t set up yet. Please contact support.'
            : 'You belong to more than one workspace. Pick the one you want to work in.'}
        </AuthHeading>

        <div className="flex flex-col gap-2.5">
          {memberships.map((membership) => {
            const isCurrent = membership.tenantSlug === current;
            const scope =
              membership.board && membership.grade
                ? `${membership.board} · Class ${gradeLabel(membership.grade)}`
                : null;

            return (
              <a
                key={membership.membershipId}
                href={pathInTenant(membership.tenantSlug, '/')}
                className="flex items-center justify-between gap-3 no-underline"
                style={{
                  padding: '13px 15px',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-default)',
                  background: isCurrent ? 'var(--surface-sunken)' : 'var(--surface-card)',
                  color: 'var(--text-body)',
                }}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold">
                    {membership.tenantType === 'FIRST_PARTY'
                      ? 'My own CWY account'
                      : membership.tenantName}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-hint)', marginTop: 2 }}>
                    {MEMBERSHIP_ROLE_LABEL[membership.role]}
                    {scope ? ` · ${scope}` : ''}
                  </div>
                </div>
                <span aria-hidden style={{ opacity: 0.5 }}>
                  {isCurrent ? 'Current' : '→'}
                </span>
              </a>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => void signOut()}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: fontPx(14),
            color: 'var(--text-hint)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          Sign out
        </button>
      </div>
    </AuthLayout>
  );
}
